import { PropsWithChildren, RefObject, useImperativeHandle } from 'react';
import {
  useForm,
  FormProvider,
  FieldValues,
  DefaultValues,
  SubmitHandler,
  Resolver,
  UseFormReturn,
} from 'react-hook-form';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import type { StandardSchemaV1 } from '@standard-schema/spec';
import { DevTool } from '@hookform/devtools';
import { ZodType } from 'zod';

import { IS_DEV_MODE, USE_DEVTOOLS } from '@/constants/env.constants';
import { SchemaProvider } from '../SchemaProvider';

type FormPropsType<T extends FieldValues> = {
  defaultValues: DefaultValues<T>;
  schema: ZodType<T>;
  onSubmit: SubmitHandler<T>;
  mode?: 'onSubmit' | 'onBlur' | 'onChange' | 'onTouched' | 'all';
  className?: string;
  isDisabled?: boolean;
  shouldUnregister?: boolean;
  methodsRef?: RefObject<UseFormReturn<T> | null>;
} & PropsWithChildren;

export const Form = <T extends FieldValues>({
  defaultValues,
  schema,
  onSubmit,
  mode = 'onChange',
  className,
  shouldUnregister = false,
  methodsRef,
  children,
}: FormPropsType<T>) => {
  const methods = useForm<T>({
    defaultValues,
    resolver: standardSchemaResolver(schema as StandardSchemaV1<T, T>) as Resolver<T, T>,
    mode,
    shouldUnregister,
  });

  useImperativeHandle(methodsRef, () => methods, [methods]);

  return (
    <FormProvider {...methods}>
      <form className={className} onSubmit={methods.handleSubmit(onSubmit)}>
        <SchemaProvider<T> schema={schema}>{children}</SchemaProvider>
      </form>
      {IS_DEV_MODE && USE_DEVTOOLS && <DevTool control={methods.control} />}
    </FormProvider>
  );
};
